/** US mobile — display as international groups: +1 212 555 0101 (Figma 5729-11899) */

function digitsOnly(value: string): string {
  return value.replaceAll(/\D/g, '')
}

/**
 * Ten-digit NANP national number.
 * A leading `1` is always the US country code (NANP area codes cannot start with 1),
 * including while the user is still typing or backspacing a formatted `+1 …` value.
 */
export function extractUsNationalDigits(value: string): string {
  let digits = digitsOnly(value)
  if (digits.startsWith('1')) {
    digits = digits.slice(1)
  }
  return digits.slice(0, 10)
}

/** Accepts 12125550101, +12125550101, +1 212 555 0101, 1 212 555 0101, (212) 555-0101, etc. */
export function isValidUsMobileNumber(value: string): boolean {
  const digits = extractUsNationalDigits(value.trim())
  if (digits.length !== 10) return false
  const areaFirst = digits.charAt(0)
  const exchangeFirst = digits.charAt(3)
  return areaFirst >= '2' && areaFirst <= '9' && exchangeFirst >= '2' && exchangeFirst <= '9'
}

/** NANP display for profile cards — e.g. (555) 010-0000 (BFP-314 TC-SP-07) */
export function formatUsNanpDisplay(value: string | null | undefined): string {
  const digits = extractUsNationalDigits(value ?? '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return formatUsMobileDisplay(value)
}

/** Format stored / complete values for display and edit fields — e.g. +1 212 555 0101 */
export function formatUsMobileDisplay(value: string | null | undefined): string {
  if (!value?.trim()) return ''

  const digits = extractUsNationalDigits(value)
  if (digits.length === 0) return ''

  if (digits.length <= 3) {
    return `+1 ${digits}`
  }
  if (digits.length <= 6) {
    return `+1 ${digits.slice(0, 3)} ${digits.slice(3)}`
  }
  return `+1 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
}

/** Apply on blur — normalizes spacing to +1 XXX XXX XXXX */
export function formatUsMobileOnBlur(value: string): string {
  return formatUsMobileDisplay(value)
}

/** @deprecated Prefer free-form onChange + formatUsMobileOnBlur on blur */
export function formatUsMobileInput(raw: string): string {
  return formatUsMobileDisplay(raw)
}

/** Compact E.164 for API/DB — e.g. +12125550101 */
export function normalizeUsMobileForStorage(value: string): string {
  const digits = extractUsNationalDigits(value.trim())
  if (digits.length !== 10) return ''
  return `+1${digits}`
}
